/**
 * crash-sheet.tsx — when the whole game cannot be drawn: the root's boundary
 * (main.tsx), around everything, the way in as much as the game. Whatever
 * failed leaves the ground and one sheet on it (problem-sheet.tsx) — no rail,
 * since the shell went with it: the coral mark, "Something went wrong", what
 * the player can count on, the message the failure threw folded under
 * Details, Reload, gold — the one thing left to do — and the version line
 * under it, plain, with no door to the admin tools, since there is no game to
 * open them in.
 *
 * In a match the sentence says what is kept: the game is the server's, so
 * nothing the team has done is lost, and Reload joins again by the same link.
 * The socket outlives the crash, since it is the service's and not React's,
 * but voice goes with the game. On the way in, before any match, there is
 * nothing to keep, and the sentence only asks to reload.
 */

import { useTranslation } from 'react-i18next';
import { useState, type ReactNode } from 'react';

import { createCrashReporter } from '../../services/crash-reports';
import { useMatchStore } from '../../domains/match';
import { ErrorBoundary } from './error-boundary';
import { ProblemSheet } from './problem-sheet';
import { VersionLine } from './version-line';
import styles from './crash-sheet.module.css';

/** The whole game's sheet on the ground, for what it threw. */
function CrashSheet({ error, supportId }: { error: unknown; supportId: string }) {
  const { t } = useTranslation();
  const inMatch = useMatchStore((s) => s.matchId !== null);
  return (
    <div className={styles.ground}>
      <ProblemSheet
        title={t('errors.game.title')}
        sentence={t(inMatch ? 'errors.game.sentence' : 'errors.game.retry')}
        error={error}
        supportId={supportId}
        foot={<VersionLine />}
      />
    </div>
  );
}

/** The root's boundary: whatever under it fails to draw leaves the crash sheet. */
export function RootBoundary({ children }: { children: ReactNode }) {
  const [report] = useState(() => createCrashReporter());
  return (
    <ErrorBoundary name="the game" fallback={(error) => <CrashSheet error={error} supportId={report.id} />} onFailed={(error, info) => report.send(error, info.componentStack ?? '')}>
      {children}
    </ErrorBoundary>
  );
}
